import React, { useState } from "react";
import PropTypes from "prop-types";

import { PROJECT_USER, USER, PROJECT_PROFILE, PROFILE } from "App/Routes";
import Link from "components/shared/Link/Link";
import ImageDialog from "components/shared/Dialogs/ImageDialog/ImageDialog";
import DeleteItemDialogContainer from "../DeleteItemDialog/DeleteItemDialogContainer";
import LoginDialogContainer from "components/shared/Dialogs/LoginDialog/LoginDialogContainer";

import Dropdown, { MenuItem } from "@trendmicro/react-dropdown";
import "@trendmicro/react-buttons/dist/react-buttons.css";
import "@trendmicro/react-dropdown/dist/react-dropdown.css";

import localize from "lang/localize";

const propTypes = {
  id: PropTypes.number,
  postId: PropTypes.number,
  userId: PropTypes.number,
  projectId: PropTypes.string,
  commentUserName: PropTypes.string,
  commentUserId: PropTypes.number,
  commentUserImage: PropTypes.string,
  commentTitle: PropTypes.string,
  commentContent: PropTypes.string,
  commentContentAttributed: PropTypes.array,
  commentVoteCount: PropTypes.number,
  commentCommentCount: PropTypes.number,
  commentBookmarkCount: PropTypes.number,
  createdAtFormatted: PropTypes.string,
  updatedAtFormatted: PropTypes.string,
  createdAt: PropTypes.string,
  updatedAt: PropTypes.string,
  boardId: PropTypes.number,
  board: PropTypes.object,
  post: PropTypes.object,
  type: PropTypes.string,
  removeComment: PropTypes.func,
  handleLike: PropTypes.func,
  isLiked: PropTypes.bool,
  handleBookmark: PropTypes.func,
  isBookmarked: PropTypes.bool,
  language: PropTypes.string,
  // login dialogs
  showLoginDialog: PropTypes.bool,
  handleOpenLoginDialog: PropTypes.func,
  handleCloseLoginDialog: PropTypes.func,
  // image dialogs
  showImageDialog: PropTypes.bool,
  handleOpenImageDialog: PropTypes.func,
  handleCloseImageDialog: PropTypes.func,
  currentImage: PropTypes.string,
};

// should use default pic

const getLink = (props) => {
  if (props.userId === props.commentUserId && props.projectId) {
    return PROJECT_PROFILE.format(props.projectId);
  } else if (props.userId === props.commentUserId) {
    return PROFILE;
  } else if (props.projectId) {
    return PROJECT_USER.format(props.projectId, props.commentUserId);
  } else {
    return USER.format(props.commentUserId);
  }
};

const CommentMenuDropdownButton = (props) => {
  if (props.userId === props.commentUserId) {
    return (
      <div className="dropdown-button-group">
        <Dropdown
          dropup={true}
          onSelect={(eventKey, event) => {
            event.preventDefault();
          }}
          pullRight={true}
        >
          <Dropdown.Toggle
            btnStyle="flat"
            noCaret={true}
            onClick={(e) => {
              e.preventDefault();
            }}
          >
            <span className="more-icon" />
          </Dropdown.Toggle>
          <Dropdown.Menu>
            <MenuItem
              eventKey={1}
              onSelect={(eventKey, event) => {
                event.preventDefault();
                props.setShowDeleteCommentDialog(true);
              }}
            >
              {localize("delete_reply_text", props.language)}
            </MenuItem>
          </Dropdown.Menu>
        </Dropdown>
      </div>
    );
  } else {
    return null;
  }
};

const getContentWithMentions = (commentContentAttributed, projectId) => {
  let contentArray = [];
  commentContentAttributed.map((message, index) => {
    if (message.type === "regular") {
      if (
        message.context === "url-internal" ||
        message.context === "url-external"
      ) {
        contentArray.push(
          <a
            key={index}
            href={message.text}
            onClick={(e) => e.stopPropagation()}
            className="link default link-color underline-on-hover"
          >
            {message.text}
          </a>,
        );
      } else {
        contentArray.push(<span key={index}>{message.text}</span>);
      }
    } else if (message.type === "user") {
      let userLink = PROJECT_USER.format(projectId, message.context);
      contentArray.push(
        <a
          key={index}
          href={userLink}
          onClick={(e) => e.stopPropagation()}
          className="link default link-color underline-on-hover"
        >
          @{message.text}
        </a>,
      );
    }
    return null;
  });
  return contentArray;
};

const PostImages = (props) => {
  return (
    <div className="discussions-embeds-container topmargin-20">
      {props.commentEmbeds.map((commentFile, index) => (
        <div
          className="embed-item"
          key={`replyImg${commentFile.embedId}-${index + 1}`}
        >
          <span className="square-image">
            <img
              src={commentFile.embedUrl}
              alt="Media"
              onClick={props.handleOpenImageDialog}
            />
          </span>
          {props.showImageDialog && props.currentImage && (
            <ImageDialogPopup
              showImageDialog={props.showImageDialog}
              handleCloseImageDialog={props.handleCloseImageDialog}
              imageUrl={props.currentImage}
            />
          )}
        </div>
      ))}
    </div>
  );
};

/**
 * Render popup dialog for challenge image
 */
const ImageDialogPopup = (props) => {
  return (
    <ImageDialog
      showModal={props.showImageDialog}
      handleCloseImageDialog={props.handleCloseImageDialog}
      img={props.imageUrl}
      overlayClassName="discussions-images-overlay"
    />
  );
};

/**
 * Render prompt login popup dialog for bookmark button
 */
const LoginDialog = (props) => {
  return (
    <LoginDialogContainer
      showModal={props.showLoginDialog}
      handleCloseLoginDialog={props.handleCloseLoginDialog}
    />
  );
};

const BookmarkButton = (props) => {
  if (!!props.userId) {
    return (
      <button
        id="bookmarkButton"
        className={
          "button icon inline rightmargin-10" +
          (props.isBookmarked ? " bookmarked" : "")
        }
        onClick={(event) => props.handleBookmark(event, props.id)}
      >
        <span className="bookmark-icon" />
      </button>
    );
  } else {
    // TODO: Show login prompt for bookmark button instead of hiding
    return (
      <div className="inline">
        <button
          id="loginBeforeBookmarkButton"
          className="button icon inline rightmargin-10"
          onClick={props.handleOpenLoginDialog}
        >
          <span className="bookmark-icon" />
        </button>
        {props.showLoginDialog && (
          <LoginDialog
            showLoginDialog={props.showLoginDialog}
            handleCloseLoginDialog={props.handleCloseLoginDialog}
          />
        )}
      </div>
    );
  }
};

const LikeButton = (props) => {
  if (!!props.userId) {
    return (
      <button
        id="likeButton"
        className={
          "button inline rightmargin-10" +
          (props.isLiked ? " liked" : "") +
          (props.commentVoteCount > 0
            ? " small button-with-text like-button"
            : " icon")
        }
        onClick={(event) => props.handleLike(event, props.id)}
      >
        <span className="like-icon" />
        {props.commentVoteCount > 0 && (
          <span className="button-text like-count">
            {props.commentVoteCount.abbreviateNumber()}
          </span>
        )}
      </button>
    );
  } else {
    return (
      <div className="inline">
        <button
          id="loginBeforeLikeButton"
          className={
            "button inline rightmargin-10" +
            (props.commentVoteCount > 0
              ? " small button-with-text like-button"
              : " icon")
          }
          onClick={props.handleOpenLikeLoginDialog}
        >
          <span className="like-icon" />
          {props.commentVoteCount > 0 && (
            <span className="button-text like-count">
              {props.commentVoteCount.abbreviateNumber()}
            </span>
          )}
        </button>
        {props.showLoginDialog && (
          <LoginDialog
            showLoginDialog={props.showLoginDialog}
            handleCloseLoginDialog={props.handleCloseLoginDialog}
          />
        )}
      </div>
    );
  }
};

const CommentMessage = (props) => {
  return (
    <div className="pure-g topmargin-5">
      <div>
        <Link to={getLink(props)} className="userphoto">
          <img
            src={props.commentUserImage}
            className="circle border-gray border-thin"
            alt={props.commentUserName}
          />
        </Link>
      </div>
      <div className="discussions-reply">
        <Link to={getLink(props)}>
          <span className="discussions-post-username">
            {props.commentUserName}
          </span>
        </Link>
        <span className="discussions-post-date leftmargin-5">
          &bull; {props.createdAtFormatted}
        </span>
        {/* API return props.postContentAttributed when URL is detected */}
        {/* Can't seem to get dangerouslySetInnerHTML to work */}
        <div className="message-content">
          {props.commentContentAttributed
            ? getContentWithMentions(
                props.commentContentAttributed,
                props.projectId,
              ).map((commentContent) => commentContent)
            : props.commentContent}
        </div>
        {/* {props.commentEmbeds && handleCommentEmbeds(props.commentEmbeds)} */}
        {props.commentEmbeds && (
          <PostImages
            commentEmbeds={props.commentEmbeds}
            handleOpenImageDialog={props.handleOpenImageDialog}
            showImageDialog={props.showImageDialog}
            handleCloseImageDialog={props.handleCloseImageDialog}
            currentImage={props.currentImage}
          />
        )}
      </div>
    </div>
  );
};

const DiscussionComment = (props) => {
  const [showDeleteCommentDialog, setShowDeleteCommentDialog] = useState(false);
  return (
    <div className="bottommargin box-sizing-border-box-all">
      <div className="discussions-reply-container">
        <div className="discussions-reply-body">
          <div className="flex-grow-1">
            <CommentMessage
              link={getLink(props)}
              projectId={props.projectId}
              commentContent={props.commentContent}
              commentContentAttributed={props.commentContentAttributed}
              commentEmbeds={props.commentEmbeds}
              commentUserName={props.commentUserName}
              commentUserImage={props.commentUserImage}
              commentTitle={props.commentTitle}
              createdAtFormatted={props.createdAtFormatted}
              // Popup for images
              handleOpenImageDialog={props.handleOpenImageDialog}
              showImageDialog={props.showImageDialog}
              handleCloseImageDialog={props.handleCloseImageDialog}
              currentImage={props.currentImage}
            />
          </div>
        </div>
        <div className="discussions-post-actions">
          <BookmarkButton
            id={props.id}
            handleBookmark={props.handleBookmark}
            isBookmarked={props.isBookmarked}
            showLoginDialog={props.showLoginDialog}
            handleOpenLoginDialog={props.handleOpenLoginDialog}
            handleCloseLoginDialog={props.handleCloseLoginDialog}
            userId={props.userId}
          />
          <LikeButton
            id={props.id}
            handleLike={props.handleLike}
            isLiked={props.isLiked}
            commentVoteCount={props.commentVoteCount}
            userId={props.userId}
          />
          <CommentMenuDropdownButton
            userId={props.userId}
            commentUserId={props.commentUserId}
            setShowDeleteCommentDialog={setShowDeleteCommentDialog}
            language={props.language}
          />
        </div>
      </div>
      <DeleteItemDialogContainer
        showDialog={showDeleteCommentDialog}
        commentId={props.id}
        isSubComment={props.isSubComment}
        handleCloseDialog={() => setShowDeleteCommentDialog(false)}
        removeComment={props.removeComment}
        type={"comment"}
        language={props.language}
      />
    </div>
  );
};

DiscussionComment.propTypes = propTypes;

export default DiscussionComment;
